import { MetadataBrowser } from '@/pages/salesforce/metadata-browser/MetadataBrowser';
import { ErrorBoundary, initBugsnag, initBugsnagPerformance } from '@/shared/libs/bugsnag';
import type { UserSession } from '@/shared/store/global';
import { GlobalStateContext } from '@/shared/store/global';
import { setActiveLocale } from '@/shared/utils/date';
import { modalError, shouldShowErrorAsModal, toastError } from '@/shared/utils/error';
import { newTimeWindowMemo } from '@/shared/utils/time_window';
import { EOneRequest, webAppApi } from '@lib/api';
import { SCComponentRoot, SCModal, SCTool } from '@lib/sc-eone-ui';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
// SC-1360: hoisted here from the layout so that SCComponentRoot is inside it — see WrapApp.
import { HelpProvider } from 'eone-ui/help-context';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useNavigate,
} from 'react-router-dom';
import { pagesRoutes } from 'virtual:react-pages';
import { Debug } from './debug';
import { initErrorHandle } from './error_handle';
import { Layout } from './layout';
import { legacyRedirectRoutes } from './legacy-redirects';
// init dayjs
import '@/shared/libs/dayjs';
import { PermissionProvider } from '../hooks/usePermissions';
import type { TeamConnectionRoleSchemaModel, TeamRoleSchemaModel } from '../pages/security/type';
declare global {
  interface Window {
    __version?: string;
    __userSession?: UserSession;
  }
}

const isDev = import.meta.env.MODE === 'development';
const proxyTarget = import.meta.env.VITE_PROXY_TARGET;

const Root = () => {
  const navigate = useNavigate();

  useEffect(() => {
    SCTool.route.setNavigate(navigate);
  }, [navigate]);

  return (
    <ErrorBoundary>
      <Layout>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </Layout>
      {/* <SCComponentRoot /> */}
    </ErrorBoundary>
  );
};

let productionPagesRoutes = pagesRoutes;
if (isDev) {
  productionPagesRoutes = productionPagesRoutes.filter((route) => route.path !== '/demo');
}

const routes = [
  {
    path: '/',
    element: <Root />,
    children: [
      {
        index: true,
        element: <Navigate to="/home" replace />,
      },
      {
        // SC-741 Salesforce Metadata Browser — rendered inside the `/` <Root> element so it gets the
        // full app shell (header, menu, sidebars, theme) from <Layout>, same as every other page.
        path: 'salesforce/metadata-browser',
        element: (
          <ErrorBoundary>
            <MetadataBrowser />
          </ErrorBoundary>
        ),
      },
      ...productionPagesRoutes,
      ...legacyRedirectRoutes,
      {
        path: '*',
        element: <Navigate to="/home" replace />,
      },
    ],
  },
];

initBugsnag();
initBugsnagPerformance(routes);

const router = createBrowserRouter(routes, {
  future: {
    v7_relativeSplatPath: true,
  },
});

const App = () => {
  // Todo: proper loading UI
  // the blocking data-fetching logics
  const [ssoReady, setSsoReady] = useState(false);
  const {
    data: checkSession,
    isSuccess: checkSuccess,
    error: checkError,
  } = useQuery({
    queryKey: ['/login/checksession'],
    queryFn: async () => {
      const res = await EOneRequest.get('/login/checksession');
      return res.data;
    },
  });
  const {
    data: userSession,
    isSuccess: userSuccess,
    error: userError,
  } = useQuery({
    // Note: user/session call requires checksession first
    enabled: ssoReady,
    queryKey: ['/user/session'],
    queryFn: async () => {
      const res = await EOneRequest.get('/api/user/session');
      // Note: access res.data?.data instead with the new session API
      if (res.data?.data) {
        window.__userSession = res.data.data;
        // Set the app-wide date/time locale before any route renders (rendering
        // is gated on this query succeeding), so dates format correctly on first paint.
        setActiveLocale(res.data.data.culture);
      }
      return res.data?.data;
    },
  });
  const {
    data: version,
    isSuccess: versionSuccess,
    error: versionError,
  } = useQuery({
    queryKey: ['home-get-version'],
    queryFn: async () => {
      const res = await webAppApi.version.getVersion();
      if (res.data) {
        window.__version = res.data;
      }
      return res.data;
    },
  });
  const [errToasted, setErrToasted] = useState(false);
  const anyError = checkError || userError || versionError;
  useEffect(() => {
    if (anyError && !errToasted) {
      if (shouldShowErrorAsModal(anyError)) {
        modalError(anyError);
      } else {
        toastError(anyError, {
          duration: 0, // toasting for the fatal error message
        });
      }
      setErrToasted(true);
    }
  }, [anyError, errToasted]);

  useEffect(() => {
    const remove = initErrorHandle();
    return () => {
      remove();
    };
  }, []);

  useEffect(() => {
    if (checkSession) {
      const is302 = typeof checkSession === 'string';
      if (is302) {
        SCModal.alert({
          Message:
            '[Development Warning] checksession API is 302-redirected. Please reload the page to continue.',
          ConfirmLabel: 'Reload',
          OnConfirm: () => {
            window.location.reload();
          },
        });
      } else if (checkSession.redirect) {
        window.location.href = checkSession.redirectUrl;
      } else if (/html>/i.test(checkSession)) {
        // Fixme: push backend to improve inproper 302-redirect behavior
        // noop
      } else {
        setSsoReady(true);
      }
    }
  }, [checkSession]);

  const globalState = useMemo(() => {
    return {
      userSession,
      version,
    };
  }, [userSession, version]);

  if (window.location.pathname.startsWith('/debug')) {
    return <Debug />;
  }

  //Role security implementation
  const fetchSchema = async () => {
    if (userSession) {
      const res = await EOneRequest.post('/api/security/team-roles', userSession.email);
      const data: { roles: TeamRoleSchemaModel[]; connectionRoles: TeamConnectionRoleSchemaModel } =
        await res?.data;
      setSchema(data);
    }
  };

  const [schema, setSchema] = useState<{
    roles: TeamRoleSchemaModel[];
    connectionRoles: TeamConnectionRoleSchemaModel;
  } | null>(null);
  useEffect(() => {
    (async () => {
      fetchSchema();
    })();
  }, [userSession]);

  const refreshContext = useCallback(async () => {
    fetchSchema();
  }, []);

  const isSuccess = checkSuccess && userSuccess && versionSuccess;
  if (!isSuccess) {
    return null;
  }

  return (
    <PermissionProvider
      schema={schema?.roles ?? null}
      connectionschema={schema?.connectionRoles ?? null}
      refreshPremissionContext={refreshContext}
    >
      <GlobalStateContext.Provider value={globalState}>
        <RouterProvider
          router={router}
          future={{
            v7_startTransition: true,
          }}
        />
      </GlobalStateContext.Provider>
    </PermissionProvider>
  );
};

// workaround to reduce frequency of toasting on the same error
const ttlToCheck = 1000 * 10;
// stopTimer is ignored here for window-scoped logic
const memo = newTimeWindowMemo(ttlToCheck);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      // Important Note: disabling refetchOnMount, refetchOnWindowFocus, refetchOnReconnect of react-query by default is important,
      // otherwise, a dirty draft or something on the server-side might be requested and loaded and overwriting the active formData unexpectedly
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      throwOnError: (error) => {
        // Note: the hack "setTimeout + throw error" here is required temporarily and can not remove
        setTimeout(() => {
          // workaround to reduce frequency of toasting on the same error
          if (memo.has(error)) return; // skip the same error for a time window
          memo.add(error);
          throw error;
        }, 0);
        return false;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

function WrapApp() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* added for some helper tools to work */}
      {(isDev && <meta name="PROXY_TARGET" content={proxyTarget} />) || null}
      {/*
        SC-1360: HelpProvider has to be an ancestor of BOTH <App /> and <SCComponentRoot />.

        SCComponentRoot is where every SCModal.* modal actually renders — it holds them in its own state and
        renders them as its children. It is a SIBLING of <App />, so anything shown through SCModal sits outside
        every provider mounted inside App: the help context, and also PermissionProvider, GlobalStateContext and
        the router. Modal content that needs any of those cannot see it.

        That is why duplicating an integration blanked the whole app. CloneModalContent is passed to
        SCModal.showModal, it renders a form field that renders eone-ui's HelpLinkButton, and that calls
        useHelpContext() — which throws by design when there is no provider above it. With no error boundary
        around modal content, the throw unmounted the entire tree.

        Only HelpProvider is hoisted, because that is the context the crash demonstrated and it depends on
        nothing else. The others are deliberately left alone: PermissionProvider needs the fetched role schema
        and GlobalStateContext needs App's state, so hoisting them is a bigger change than this fix, and
        hoisting speculatively is how you break something that works. If a modal is ever given content that
        needs one of them, it will fail the same way, and this comment is the map.

        SC-830 added the missing safety net: baseRenderModal now wraps modal children in SCErrorBoundary, so
        a throw in modal content no longer unmounts SCComponentRoot. Note what that means for the failure
        mode above — an unmount here is permanent, because SCComponentRoot's cleanup resets
        SCModal.renderComponent to a console.warn no-op, so every modal opened afterwards silently renders
        nothing. The boundary stops the cascade; it does not make a missing provider work, so the reasoning
        about which providers belong above SCComponentRoot still stands.
      */}
      <HelpProvider>
        <App />
        <SCComponentRoot />
      </HelpProvider>
    </QueryClientProvider>
  );
}

export default WrapApp;
